import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Card, CardContent, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import './CSMSelector.css';

const CSMSelector = ({ clientId, onCSMChange }) => {
    const [availableCSMs, setAvailableCSMs] = useState([]);
    const [assignedCSM, setAssignedCSM] = useState(null);
    const [isCSMLoading, setIsCSMLoading] = useState(false);
    const [openRemoveCSMModal, setOpenRemoveCSMModal] = useState(false);
    const [expanded, setExpanded] = useState(true);

    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;
    const user = Auth.getUser();
    const isAdmin = user?.admin || false;

    useEffect(() => {
        const fetchClientCSM = async () => {
            if (!clientId || !workspaceId) return;
            
            try {
                const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                    params: {
                        clientId: clientId,
                        workspaceId: workspaceId
                    }
                });
                    setAssignedCSM(response.data.csmName === 'unassigned' ? null : response.data.csmName || null);
            } catch (err) {
                console.error("Failed to fetch client CSM:", err);
            }
        };

        const fetchAvailableCSMs = async () => {
            if (!workspaceId || !userId || !isAdmin) return;
            
            try {
                const response = await axios.get(
                    `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`
                );
                setAvailableCSMs(response.data);
            } catch (err) {
                console.error("Failed to fetch available CSMs:", err);
                setAvailableCSMs([]);
            }
        };

        fetchClientCSM();
        fetchAvailableCSMs();
    }, [clientId, workspaceId, userId, isAdmin]);

    const refreshAvailableCSMs = async () => {
        if (!workspaceId || !userId || !isAdmin) return;
        try {
            const response = await axios.get(
                `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`
            );
            setAvailableCSMs(response.data);
        } catch (err) {
            console.error("Failed to refresh available CSMs:", err);
        }
    };

    const handleCSMAssignment = async (csmId) => {
        if (!csmId || isCSMLoading) return;
        
        setIsCSMLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/clients/assign-csm`,
                {
                    workspaceId: Number(workspaceId),
                    clientId: Number(clientId),
                    assignedUserId: Number(csmId),
                    adminUserId: Number(userId)
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            setAssignedCSM(
                response.data.csmInfo.firstName && response.data.csmInfo.lastName 
                    ? `${response.data.csmInfo.firstName} ${response.data.csmInfo.lastName}`
                    : response.data.csmInfo.email
            );
            toast.success('CSM assigned successfully!');
            await refreshAvailableCSMs();
            
            if (onCSMChange) {
                onCSMChange();
            }
        } catch (err) {
            console.error('Error assigning CSM:', err);
            toast.error('Error assigning CSM');
        } finally {
            setIsCSMLoading(false);
        }
    };

    const handleCSMRemoval = async () => {
        if (isCSMLoading) return;
        
        setIsCSMLoading(true);
        
        const payload = {
            workspaceId: Number(workspaceId),
            clientId: Number(clientId),
            adminUserId: Number(userId)
        };
        
        try {
            const response = await fetch(`${API_URL}/clients/remove-csm`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorData}`);
            }
            
            setAssignedCSM(null);
            toast.success('CSM removed successfully!');
            await refreshAvailableCSMs();
            
            if (onCSMChange) {
                onCSMChange();
            }
        } catch (err) {
            console.error('Error removing CSM:', err);
            toast.error('Error removing CSM');
        } finally {
            setIsCSMLoading(false);
        }
    };

    const handleCSMChange = async (event) => {
        const selectedValue = event.target.value;
        
        if (selectedValue) {
            await handleCSMAssignment(selectedValue);
        }
    };

    const handleAccordionChange = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <>
            <Card sx={{ 
                width: '100%', 
                maxWidth: 800, 
                boxShadow: 'none', 
                border: 'none', 
                borderRadius: '2%', 
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'height 0.3s ease',
                marginBottom: '15px'
            }}>
                <CardContent sx={{ padding: '0 !important' }}>
                    <Accordion 
                        expanded={expanded}
                        onChange={handleAccordionChange}
                        sx={{ 
                            boxShadow: 'none', 
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                                margin: 0
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                padding: '16px',
                                '& .MuiAccordionSummary-content': {
                                    margin: 0,
                                    alignItems: 'center'
                                }
                            }}
                        >
                            <span className="csm-selector-label" style={{ margin: 0, marginTop: 0, marginBottom: 0 }}>
                                Assigned CSM
                            </span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: '0 16px 16px 16px' }}>
                            <div className="csm-selector-wrapper">
                                <FormControl className="csm-selector-form-control">
                                    <Select
                                        value=""
                                        onChange={handleCSMChange}
                                        displayEmpty
                                        disabled={isCSMLoading}
                                        renderValue={() => {
                                            if (assignedCSM && assignedCSM.trim() !== '' && assignedCSM !== 'unassigned') {
                                                return (
                                                    <span className="csm-selector-assigned">
                                                        {assignedCSM}
                                                    </span>
                                                );
                                            }
                                            return "Select CSM";
                                        }}
                                        className="csm-selector-select"
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 10 * 48,
                                                    width: 'auto', 
                                                    maxWidth: '400px'
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem disabled value="">
                                            Select CSM
                                        </MenuItem>
                                        {availableCSMs.map((csm) => (
                                            <MenuItem 
                                                key={csm.id} 
                                                value={csm.id}
                                                className="csm-selector-menu-item"
                                            >
                                                <span className="csm-selector-name">
                                                    {csm.firstName && csm.lastName 
                                                        ? `${csm.firstName} ${csm.lastName}`
                                                        : csm.email}
                                                    <span className="csm-selector-client-count">
                                                        ({csm.clientCount} clients)
                                                    </span>
                                                </span>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {(() => {
                                    const trimmedCSM = assignedCSM ? assignedCSM.trim() : '';
                                    return trimmedCSM !== '' && trimmedCSM.toLowerCase() !== 'unassigned';
                                })() && (
                                    <span
                                        className="csm-selector-remove-btn"
                                        onClick={() => setOpenRemoveCSMModal(true)}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </span>
                                )}
                            </div>
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>

            {/* Remove CSM Modal */}
            <Dialog open={openRemoveCSMModal} onClose={() => setOpenRemoveCSMModal(false)}>
                <DialogTitle>Remove CSM Assignment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove the current CSM assignment from this client?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRemoveCSMModal(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        await handleCSMRemoval();
                        setOpenRemoveCSMModal(false);
                    }} color="error">Remove</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CSMSelector;